import type { PropTypes } from './MarketingCampaign.types';
import type { ReactElement } from 'react';

import { Card, Flex } from '@rango-dev/whale';
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DEFAULT_PRIZE_POOL_LOGO, PRIZE_POOL_ICONS } from '../../../constants/campaign';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { fetchCampaignLeaderboard, fetchUserData } from '../../../state/CampaignsSlice';
import { fetchDynamicCampaign } from '../../../state/CampaignsSlice/thunks';
import { getCampaignContent, resolveStrapiImage } from '../../../utils/campaign';
import { Article } from '../Article';
import { Banner } from '../Banner';
import { Leaderboard } from '../Leaderboard';
import { PrizePoolBox } from '../PrizePoolBox';
import { TaskBox } from '../TaskBox';

import styles from './MarketingCampaign.module.css';
import { MarketingCampaignSkeleton } from './MarketingCampaignSkeleton';

const LEADERBOARD_PAGE_SIZE = 20;

export function MarketingCampaign(props: PropTypes): ReactElement {
  const { campaignId } = props;

  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { isLoggedIn } = useAppSelector((state) => state.profile);
  const campaignData = useAppSelector((state) => state.campaign.entries?.[campaignId]);

  const { dynamicData, dataDetails, loadingDetails, dynamicArticle } =
    getCampaignContent(campaignData);

  const pageParams = Number(searchParams.get('page'));
  const page = pageParams ? pageParams - 1 : 0;

  const campaignStart = new Date(dataDetails?.start || '').getTime();
  const isBeforeStartCampaign = Date.now() < campaignStart;

  useEffect(() => {
    const request = dispatch(fetchDynamicCampaign({ id: campaignId }));

    return () => {
      request?.abort?.();
    };
  }, [campaignId, dispatch]);

  useEffect(() => {
    const request = dispatch(
      fetchCampaignLeaderboard({ page, offset: LEADERBOARD_PAGE_SIZE, id: campaignId }),
    );

    return () => {
      request?.abort?.();
    };
  }, [campaignId, page, dispatch]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    const request = dispatch(fetchUserData({ id: campaignId }));

    return () => {
      request?.abort?.();
    };
  }, [isLoggedIn, campaignId, dispatch]);

  const handlePageNavigation = (pageNumber: number) => {
    setSearchParams({ page: (pageNumber + 1).toString() });
  };

  if (!dynamicData) {
    return <MarketingCampaignSkeleton />;
  }

  return (
    <Flex direction="column" width="100%" gap={{ initial: '4', md: '5' }}>
      <Banner
        detailsData={dataDetails || null}
        detailsLoading={loadingDetails}
        campaignId={campaignId}
        title={dynamicData.title}
        description={dynamicData.description}
        image={resolveStrapiImage(dynamicData.imageBanner)}
        backgroundImage={resolveStrapiImage(dynamicData.backgroundBanner)}
      />
      <Flex
        direction={{ initial: 'column', xl: 'row' }}
        gap={{ initial: '4', md: '5' }}
        className={styles.extra}>
        <TaskBox
          className={styles.taskCell}
          detailsData={dataDetails || null}
          detailsLoading={loadingDetails}
          campaignId={campaignId}
          title={dynamicData.taskTitle}
          description={dynamicData.taskDescription}
          image={resolveStrapiImage(dynamicData.taskImage)}
          logo={resolveStrapiImage(dynamicData.logo)}
        />
        <PrizePoolBox
          className={styles.prizeCell}
          detailsData={dataDetails || null}
          detailsLoading={loadingDetails}
          campaignId={campaignId}
          title={dynamicData.prizePoolTitle}
          description={dynamicData.prizePoolDescription}
          logo={resolveStrapiImage(dynamicData.prizePoolImage) || DEFAULT_PRIZE_POOL_LOGO}
          prizePoolIcons={PRIZE_POOL_ICONS}
          type={dynamicData.type}
        />
      </Flex>
      {!isBeforeStartCampaign && (
        <Card size={{ initial: '1', md: '3' }}>
          <Leaderboard
            handlePageNavigation={handlePageNavigation}
            page={page}
            campaignId={campaignId}
            type={dynamicData.type}
            leaderboardData={campaignData?.leaderboard?.data || null}
            leaderboardLoading={campaignData?.leaderboard?.loading}
            leaderboardTotalPages={campaignData?.leaderboard?.totalPages || null}
            userData={campaignData?.userData?.data || null}
          />
        </Card>
      )}
      {dynamicArticle && <Article dynamicArticle={dynamicArticle} />}
    </Flex>
  );
}
